// src\components\Payroll\ProcessPartialSalaryFld\ProcessPartialSalary.js
import React, { useState, useRef, useEffect } from "react";
import SettingsIcon from "@mui/icons-material/Settings";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import PostAddIcon from "@mui/icons-material/PostAdd";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PaidIcon from "@mui/icons-material/Paid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { EmployeeDtrModal } from "./shared/EmployeeDtrModal";
import { EditEligibilityForm } from "./shared/EditEligibilityForm";

// MODIFIED: using CreateTableNew, SearchBar, GeneralMessage, useGeneralMessage from APICommon
import {
  CreateTableNew as CreateTable,
  SearchBar,
  GeneralMessage,
  useGeneralMessage,
} from "../../../common/APICommon";
import { StatusBadge } from "../../../common/Badge";
import { useProcessPartialSalary } from "./shared/useProcessPartialSalary";
import { ProcessBatchForm } from "./shared/ProcessBatchForm";
import { RevertBatchForm } from "./shared/RevertBatchForm";
import { LoadingSpinner } from "../../../common/Loading";
import { PARTIALSALARYSTATUS } from "../../../utils/enums";
import { generatePartialSalaryPDF } from "./shared/generatePartialSalaryPDF";

// ── Table config — shared by Processed + Posted tabs ─────────────────────────
const tableProcessedHeaders = ["Employee ID", "Employee Name", "Employee Type", "Position", "Amount", "Is Eligible?", "Actions"];
const tableProcessedAccessors = ["employeeId", "employeeFullName", "employeeType", "position", "amount", "isEligible", "actions"];

const tablePostedHeaders = ["Employee ID", "Employee Name", "Employee Type", "Position", "Amount"];
const tablePostedAccessors = ["employeeId", "employeeFullName", "employeeType", "position", "amount"];

const allowPostPartialSalary = false; // ADDED: toggle for Post Partial Salary action

// ── CustomLoader — mirrors payroll.js — used only for Process, Post, Revert ──
function CustomLoader({ loadingPercentage }) {
  return (
    <div style={{
      position: "fixed", top: 0, left: 0,
      width: "100vw", height: "100vh",
      backgroundColor: "#ffffff59", zIndex: 10000,
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
    }}>
      {loadingPercentage !== 0 && (
        <p style={{ color: "rgb(104,104,104)" }}>{`Loading: ${loadingPercentage}%`}</p>
      )}
      <div className="custom-loader">
        <div style={{
          position: "absolute", top: 0, left: 0,
          height: "100%", width: `${loadingPercentage}%`,
          backgroundColor: "rgba(255,255,255,0.8)", borderRadius: "inherit",
        }} />
      </div>
    </div>
  );
}

// ── SettingInit — mirrors payroll.js SettingInit ───────────────────────────────
function SettingInit({ openProcessForm, openPostForm, openRevertForm }) {
  return (
    <div id="payroll-setting" className="payroll-setting column">
      <div className="payroll-setting-item row" onClick={openProcessForm}>
        <div style={{ margin: "10px 5px 0 5px", color: "#ffaa00" }}><AccountTreeIcon /></div>
        Process Partial Salary
      </div>
      {allowPostPartialSalary && (
        <div className="payroll-setting-item row" onClick={openPostForm}>
          <div style={{ margin: "10px 5px 0 5px", color: "#1a75ff" }}><PostAddIcon /></div>
          Post Partial Salary
        </div>
      )}
      <div className="payroll-setting-item row" onClick={openRevertForm}>
        <div style={{ margin: "10px 5px 0 5px", color: "#ff4d4d" }}><SettingsBackupRestoreIcon /></div>
        Revert Staging
      </div>
    </div>
  );
}

// ── Tab button ────────────────────────────────────────────────────────────────
function TabButton({ label, active, onClick }) {
  return (
    <button onClick={onClick} id={active ? "btn-tab-active" : ""} style={{ marginRight: "4px" }}>
      {label}
    </button>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
function ProcessPartialSalary({ user, isHideSideMenu }) {
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  const {
    loadingProcessed,
    loadingPosted,
    requestLoading,
    loadingPercentage,
    loadingSpinner, // ADDED
    batches,
    processedData,
    postedData,
    filterBatchId,
    selectedEmployee,
    showDtrModal,
    setShowDtrModal,
    setFilterBatchId,
    setIsGenerate,
    handleProcess,
    handlePost,
    handleRevert,
    openDtrView,
    showEditForm,
    selectedEditRow,
    setShowEditForm,
    openEditForm,
    handleEditEligibility,
  } = useProcessPartialSalary({ user, showGeneralMessage });

  const [showSetting, setShowSetting] = useState(false);
  const settingRef = useRef(null);
  const buttonRef = useRef(null);

  const [showProcessForm, setShowProcessForm] = useState(false);
  const [showPostForm, setShowPostForm] = useState(false);
  const [showRevertForm, setShowRevertForm] = useState(false);
  const [activeTab, setActiveTab] = useState("processed");

  // ── Pagination per tab ────────────────────────────────────────────────────
  const [processedPage, setProcessedPage] = useState(1);
  const [postedPage, setPostedPage] = useState(1);

  // ── Search filter state per tab ───────────────────────────────────────────
  const [processedFiltered, setProcessedFiltered] = useState([]);
  const [postedFiltered, setPostedFiltered] = useState([]);

  // -- 
  const selectedBatch = batches.find((b) => String(b.id) === String(filterBatchId));

  // ADDED: batch options for the Select dropdown — mirrors Client.js pattern
  const batchOptions = batches.map((b) => ({
    value: b.id,
    label: `Batch #${b.id} | ${b.effectiveDate} | ${b.status ?? "—"}`,
  }));

  const processedActions = [
    {
      name: "View DTR",
      icon: <VisibilityIcon fontSize="small" />,
      className: "bg-blue-500 hover:bg-blue-600 flex items-center justify-center text-white p-1 rounded",
      function: (row) => openDtrView(row),
      visible: true,
    },
    {
      name: "Edit Eligibility",
      icon: <EditIcon fontSize="small" />,
      className: "bg-green-500 hover:bg-green-600 flex items-center justify-center text-white p-1 rounded",
      function: (row) => openEditForm(row),
      visible: () => (selectedBatch.status === PARTIALSALARYSTATUS.STAGING) ?? false,
    },
  ];

  // ADDED: sync filter state when data changes
  useEffect(() => { setProcessedFiltered(processedData); }, [processedData]);
  useEffect(() => { setPostedFiltered(postedData); }, [postedData]);

  const buildTableData = (rows) =>
    rows.map((row, index) => ({
      ...row,
      employeeFullName: row.employeeFullName || "—",
      employeeType: row.employeeType || "—",
      position: row.position || "—",
      amount: row.amount != null
        ? row.amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : "—",
      payrollPeriodDescription: row.payrollPeriodDescription || `Period ${row.payrollPeriodId || "—"}`,
      isEligibleRaw: row.isEligible, // ADDED: preserve raw boolean for edit modal pre-population
      isEligible: row.isEligible === true ? (
        <StatusBadge status={"Yes"} />
      ) : (
        <StatusBadge status={"No"} />
      ),
      effectiveDate: row.effectiveDate ? row.effectiveDate.split("T")[0] : "—",
    }));

  // ADDED: search handler — mirrors Client.js handleFilter pattern
  const handleProcessedFilter = (value) => {
    setProcessedPage(1);
    if (!value) { setProcessedFiltered(processedData); return; }
    const lower = value.toLowerCase();
    setProcessedFiltered(
      processedData.filter((r) =>
        r.employeeFullName?.toLowerCase().includes(lower) ||
        r.employeeType?.toLowerCase().includes(lower) ||
        r.position?.toLowerCase().includes(lower)
      )
    );
  };

  const handlePostedFilter = (value) => {
    setPostedPage(1);
    if (!value) { setPostedFiltered(postedData); return; }
    const lower = value.toLowerCase();
    setPostedFiltered(
      postedData.filter((r) =>
        r.employeeFullName?.toLowerCase().includes(lower) ||
        r.employeeType?.toLowerCase().includes(lower) ||
        r.position?.toLowerCase().includes(lower)
      )
    );
  };

  const handleGenerate = () => {
    if (!filterBatchId) {
      showGeneralMessage("Please select a batch first!", false);
      return;
    }
    setIsGenerate(true);
  };

  const handleSetting = () => setShowSetting(!showSetting);

  // ── Gear icon toggle — mirrors payroll.js ─────────────────────────────────
  useEffect(() => {
    const settingIcon = document.getElementById("ps-setting-icon");
    if (!settingIcon) return;
    if (showSetting) {
      settingIcon.setAttribute("data-testid", "KeyboardArrowUpIcon");
      settingIcon.children[0]?.setAttribute("d", `M7.41 15.41 12 10.83l4.59 4.58L18 14l-6-6-6 6z`);
    } else {
      settingIcon.setAttribute("data-testid", "KeyboardArrowDownIcon");
      settingIcon.children[0]?.setAttribute("d", `M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6z`);
    }
    const handleClickOutside = (event) => {
      if (settingRef.current && !settingRef.current.contains(event.target)) {
        if (buttonRef.current && !buttonRef.current.contains(event.target)) {
          setShowSetting(false);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSetting]);


  return (
    <div className={`MainBody row ${isHideSideMenu ? "MainBody2" : ""}`}>
      <div className="LeftSpace" />
      <div className={`BodySpace ${isHideSideMenu ? "BodySpace2" : ""}`}>
        <div className="PayrollInfo shadow-xl">

          {/* ── Breadcrumb ──────────────────────────────────────────────── */}
          <div className="sub-title row" style={{ padding: "5px 20px" }}>
            <span style={{ padding: "0 5px 0 0", color: "#b0b0b0" }}>Payroll {">"} </span>
            <span style={{ borderBottom: "1px solid black" }}>Process Partial Salary</span>
          </div>

          {/* ── Header ──────────────────────────────────────────────────── */}
          <div className="payrollHeader row">
            <div style={{ width: "70%", color: "#668fca" }} className="payroll-title row">
              <PaidIcon style={{ padding: "0 10px 0 0" }} />
              <h3>Process Partial Salary</h3>
            </div>
            <div style={{ width: "30%", textAlign: "right" }}>
              <button ref={buttonRef} onClick={handleSetting} title="Setting" type="button" className="setting">
                <SettingsIcon />
                <KeyboardArrowDownIcon id="ps-setting-icon" />
              </button>
              <div ref={settingRef}>
                {showSetting && (
                  <SettingInit
                    openProcessForm={() => { setShowSetting(false); setShowProcessForm(true); }}
                    openPostForm={() => { setShowSetting(false); setShowPostForm(true); }}
                    openRevertForm={() => { setShowSetting(false); setShowRevertForm(true); }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* ── Body ────────────────────────────────────────────────────── */}
          <div className="payroll-body">
            <div style={{ padding: "10px" }}>

              {/* ── Filter ──────────────────────────────────────────────── */}
              <div className="filters-payroll">
                <span>Filter</span>
                <div
                  className="payroll-item-holder info-item-holder row"
                  style={{ justifyContent: "start", padding: "0 10px 20px 10px" }}
                >
                  <div className="filter-item-payroll popUp-item info-item column">
                    <div className="info-item-label">Batch</div>
                    <div className="info-item-input">
                      <select
                        value={filterBatchId}
                        onChange={(e) => setFilterBatchId(e.target.value)}
                        style={{ color: filterBatchId ? "rgb(104,104,104)" : "rgba(0,0,0,0.356)" }}
                      >
                        <option value="" disabled hidden>Select a batch</option>
                        {batchOptions.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="rightSide-btn" style={{ marginRight: "5px" }}>
                  <button
                    className="generate row"
                    onClick={handleGenerate}
                    style={{ display: "flex", alignItems: "center", gap: "4px" }}
                  >
                    <RestartAltIcon /> GENERATE
                  </button>
                </div>
              </div>

              {/* ── Selected Batch Info Card ─────────────────────────────── */}
              {selectedBatch && (
                <div
                  className="mt-[10px] px-4 py-3 bg-sky-50 !border !border-sky-200 rounded-lg flex gap-8 flex-wrap text-[13px] text-sky-900 items-center"
                  style={{ border: "1px solid #bae6fd" }}
                >
                  <div>
                    <b>Payroll Period:</b>{" "}
                    {`${selectedBatch.payrollPeriodName} | ${selectedBatch.payrollPeriodIdentifier}`}
                  </div>
                  <div>
                    <b>Vessel:</b>{" "}
                    {selectedBatch.vesselName
                      ? `${selectedBatch.vesselCode} - ${selectedBatch.vesselName}`
                      : "—"}
                  </div>
                  <div>
                    <b>Effective Date:</b>{" "}
                    {selectedBatch.effectiveDate
                      ? selectedBatch.effectiveDate.split("T")[0]
                      : "—"}
                  </div>
                  <div className="flex items-center gap-2">
                    <b>Status:</b>
                    <StatusBadge status={selectedBatch.status} />
                  </div>
                </div>
              )}

              {/* ── Tab switcher ─────────────────────────────────────────── */}
              {allowPostPartialSalary && (
                <div className="payroll-tab-header row" style={{ marginTop: "16px" }}>
                  <div className="payroll-tab-leftSide row">
                    <div className="leftSide-btn">
                      <TabButton label="PROCESSED" active={activeTab === "processed"} onClick={() => setActiveTab("processed")} />
                    </div>
                    <div className="leftSide-btn">
                      <TabButton label="POSTED" active={activeTab === "posted"} onClick={() => setActiveTab("posted")} />
                    </div>
                  </div>
                </div>
              )}

              {/* ── Tab panels ───────────────────────────────────────────── */}
              <div className="payroll-tables" style={{ marginTop: "8px" }}>

                {/* PROCESSED tab */}
                <div style={{ display: activeTab === "processed" ? "block" : "none" }}>
                  <div className="payroll-table2 container">
                    <div className="flex justify-between items-center mb-4 gap-4">
                      <div className="flex-1">
                        <SearchBar handleFilter={handleProcessedFilter} />
                      </div>

                      {processedData.length > 0 && selectedBatch && (
                        <button
                          onClick={() => generatePartialSalaryPDF(selectedBatch, buildTableData(processedFiltered))}
                          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg shadow-sm transition-colors text-sm font-medium h-[38px]"
                        >
                          <FileDownloadIcon fontSize="small" />
                          DOWNLOAD PDF
                        </button>
                      )}
                    </div>
                    <CreateTable
                      tableName="ProcessedPartialSalaryTable"
                      headers={tableProcessedHeaders}
                      accessors={tableProcessedAccessors}
                      data={buildTableData(processedFiltered)}
                      actions={processedActions}
                      currentPage={processedPage}
                      setCurrentPage={setProcessedPage}
                      itemsPerPage={10}
                      totalItems={processedFiltered.length}
                      loading={loadingProcessed}
                    />
                  </div>
                </div>

                {/* POSTED tab */}
                {allowPostPartialSalary && (
                  <div style={{ display: activeTab === "posted" ? "block" : "none" }}>
                    <div className="payroll-table2 container">
                      <SearchBar handleFilter={handlePostedFilter} />
                      <CreateTable
                        tableName="PostedPartialSalaryTable"
                        headers={tablePostedHeaders}
                        accessors={tablePostedAccessors}
                        data={buildTableData(postedFiltered)}
                        actions={[]}
                        currentPage={postedPage}
                        setCurrentPage={setPostedPage}
                        itemsPerPage={10}
                        totalItems={postedFiltered.length}
                        loading={loadingPosted}
                      />
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Action Forms ──────────────────────────────────────────────────── */}
      <ProcessBatchForm
        isOpen={showProcessForm}
        onClose={() => setShowProcessForm(false)}
        batches={batches}
        onProcess={handleProcess}
      />

      <RevertBatchForm
        isOpen={showRevertForm}
        onClose={() => setShowRevertForm(false)}
        batches={batches}
        onRevert={handleRevert}
      />

      <EmployeeDtrModal
        isOpen={showDtrModal}
        onClose={() => setShowDtrModal(false)}
        employee={selectedEmployee}
        batchId={filterBatchId}
      />

      <EditEligibilityForm
        isOpen={showEditForm}
        onClose={() => setShowEditForm(false)}
        employee={selectedEditRow}
        onSubmit={handleEditEligibility}
      />

      <GeneralMessage form={showMessage} message={message} isSuccess={isSuccess} />

      {/* MODIFIED: CustomLoader only for Process / Post / Revert */}
      {requestLoading && <CustomLoader loadingPercentage={loadingPercentage} />}

      {/* ADDED: LoadingSpinner for fetches and edit eligibility */}
      <LoadingSpinner loading={loadingSpinner} />

    </div>
  );
}

export default ProcessPartialSalary;